import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  STUDENT_DIRECTORY_SORTS,
  type StudentDirectorySort,
} from "@wmf/shared/studentDirectory";
import {
  get,
  getStudentFilterOptions,
  type StudentFilterOptions,
  type StudentList,
  type StudentSummary,
} from "../api.js";
import { canManageSchoolDomain, isOwner, useAuth } from "../auth.js";
import { formatDate, formatTime } from "../format.js";
import { ImportStudents } from "../ImportStudents.js";
import { NewStudent } from "../NewStudent.js";
import {
  addSavedStudentView,
  builtInStudentViews,
  deleteSavedStudentView,
  paramsForStudentView,
  readSavedStudentViews,
  renameSavedStudentView,
  sameStudentViewState,
  savedStudentViewNameTaken,
  STUDENT_PAGE_SIZES,
  studentViewStateForId,
  studentViewStateFromParams,
  writeSavedStudentViews,
  type SavedStudentView,
  type StudentListScope,
  type StudentViewState,
} from "../studentViews.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";

export type { StudentListScope } from "../studentViews.js";
export type StudentAdminToolMode = "admin" | "tutor-private" | null;

export interface StudentDirectorySummary extends StudentSummary {
  school: string | null;
  nextLessonAt: string | null;
  lastLessonAt: string | null;
  /** Portal activity only: this is the authoritative students.last_login_at field. */
  lastActiveAt: string | null;
}

type StudentDirectoryList = Omit<StudentList, "students"> & {
  students: StudentDirectorySummary[];
};

export interface StudentDirectoryNavigationState {
  studentDirectoryQuery?: string;
}

export function resolveStudentListScope(
  owner: boolean,
  tutor: boolean,
  requested: string | null,
): StudentListScope {
  if (!owner) return "mine";
  if (tutor && requested === "mine") return "mine";
  return "all";
}

/**
 * Student administration belongs to the resolved workspace, not the raw URL.
 *
 * Owners only get the whole-school controls in All students. An owner who also
 * teaches gets no creation/import controls in My students, while an ordinary
 * tutor keeps the scoped private-student action they already had.
 */
export function studentAdminToolMode(
  owner: boolean,
  tutor: boolean,
  scope: StudentListScope,
): StudentAdminToolMode {
  if (owner) return scope === "all" ? "admin" : null;
  return tutor ? "tutor-private" : null;
}

export function resolveStudentDirectoryPage(requested: string | null): number {
  const page = Number(requested);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/** The exact safe directory URL to return to after opening a student action. */
export function studentListReturnHref(searchParams: URLSearchParams): string {
  const next = new URLSearchParams(searchParams);
  // `new=1` is a one-shot launch request, not directory state worth replaying.
  next.delete("new");
  const suffix = next.toString();
  return suffix === "" ? "/students" : `/students?${suffix}`;
}

/**
 * Free-text directory search is intentionally not serialised into the URL.
 * Carry it through normal in-app navigation state instead, preserving #262's
 * privacy decision while still restoring the working set on return.
 */
export function studentDirectoryNavigationState(
  query: string,
): StudentDirectoryNavigationState | undefined {
  return query === "" ? undefined : { studentDirectoryQuery: query };
}

export function studentDirectoryQueryFromState(state: unknown): string {
  if (typeof state !== "object" || state === null) return "";
  const query = (state as StudentDirectoryNavigationState)
    .studentDirectoryQuery;
  return typeof query === "string" && query.length <= 100 ? query : "";
}

export function studentDetailHref(
  studentId: string,
  scope: StudentListScope,
  returnTo?: string,
): string {
  const base = `/students/${encodeURIComponent(studentId)}`;
  const params = new URLSearchParams();
  if (scope === "mine") params.set("scope", "mine");
  if (returnTo) params.set("return", returnTo);
  const suffix = params.toString();
  return suffix === "" ? base : `${base}?${suffix}`;
}

/** Reuse the canonical student record's booking workspace under Lessons. */
export function studentBookLessonHref(
  studentId: string,
  scope: StudentListScope,
  returnTo?: string,
): string {
  const params = new URLSearchParams({ tab: "lessons", book: "1" });
  if (scope === "mine") params.set("scope", "mine");
  if (returnTo) params.set("return", returnTo);
  return `/students/${encodeURIComponent(studentId)}?${params}`;
}

/** A tutor filter belongs to the whole-school table, never the already-scoped roster. */
export function studentListTutorFilter(
  scope: StudentListScope,
  tutor: string,
): string {
  return scope === "all" ? tutor : "";
}

/** The most useful email to put on a tutor-facing roster card. */
export function studentRosterContact(
  student: Pick<StudentSummary, "studentEmail" | "parentEmail">,
): string | null {
  return student.studentEmail ?? student.parentEmail;
}

export function studentContactHref(email: string): string {
  return `mailto:${encodeURIComponent(email)}`;
}

/**
 * One concise line per teaching relationship.
 *
 * StudentSummary already carries the server's enrolment projection. Keeping
 * this as display-only text means the roster does not try to infer ownership
 * from historic bookings or from the old single instrument/tutor fields.
 */
export function studentRosterLearning(
  student: Pick<StudentSummary, "enrolments" | "instrument">,
): string[] {
  if (student.enrolments.length === 0) return [student.instrument ?? "Lessons"];
  return student.enrolments.map((enrolment) => {
    const subject = enrolment.instrument ?? enrolment.serviceName ?? "Lessons";
    return enrolment.tutorName
      ? `${subject} · ${enrolment.tutorName}`
      : `${subject} · no tutor`;
  });
}

/** Concise, authoritative attention signals available on the directory projection. */
export function studentDirectoryAttention(
  student: Pick<
    StudentDirectorySummary,
    "status" | "nextLessonAt" | "hubAccess"
  >,
): string | null {
  const attention: string[] = [];
  if (student.status !== "active") attention.push(`Status: ${student.status}`);
  if (student.nextLessonAt === null) attention.push("No upcoming lesson");
  if (!student.hubAccess.studentAccountLinked && student.hubAccess.familyAccountCount === 0) {
    attention.push("No Hub account linked");
  }
  return attention.length === 0 ? null : attention.join(" · ");
}

function lessonLabel(iso: string | null): string {
  return iso === null
    ? "None booked"
    : `${formatDate(iso)}, ${formatTime(iso)}`;
}

export function studentHubAccessLabel(
  hubAccess: StudentDirectorySummary["hubAccess"],
): string {
  if (hubAccess.studentAccountLinked && hubAccess.familyAccountCount > 0) {
    return "Student + family linked";
  }
  if (hubAccess.studentAccountLinked) return "Student account linked";
  if (hubAccess.familyAccountCount > 0) return "Family account linked";
  return "Not linked";
}

function portalLabel(student: StudentDirectorySummary): string {
  const label = studentHubAccessLabel(student.hubAccess);
  return student.lastActiveAt ? `${label} · ${formatDate(student.lastActiveAt)}` : label;
}

/**
 * What a student learns and who teaches it.
 *
 * One line per instrument, because a student taking singing from one tutor and
 * drums from another is two facts, and collapsing them to one loses whichever
 * happens to be second.
 */
function Learning({ student }: { student: StudentSummary }) {
  return (
    <>
      {studentRosterLearning(student).map((line, index) => (
        <div key={`${line}:${index}`}>{line}</div>
      ))}
    </>
  );
}

function StudentActions({
  student,
  scope,
  returnTo,
  canBook,
  navigationState,
}: {
  student: StudentDirectorySummary;
  scope: StudentListScope;
  returnTo: string;
  canBook: boolean;
  navigationState: StudentDirectoryNavigationState | undefined;
}) {
  const contact = studentRosterContact(student);
  return (
    <nav
      className="student-row-actions"
      aria-label={`Actions for ${student.displayName ?? student.fullName}`}
    >
      <Link
        className="workflow-action-link"
        to={studentDetailHref(student.id, scope, returnTo)}
        state={navigationState}
      >
        Open student
      </Link>
      {canBook && (
        <Link
          className="workflow-action-link"
          to={studentBookLessonHref(student.id, scope, returnTo)}
          state={navigationState}
        >
          Book lesson
        </Link>
      )}
      {contact && (
        <a className="workflow-action-link" href={studentContactHref(contact)}>
          Contact
        </a>
      )}
    </nav>
  );
}

function StudentRosterCard({
  student,
  scope,
  returnTo,
  canBook,
  navigationState,
}: {
  student: StudentDirectorySummary;
  scope: StudentListScope;
  returnTo: string;
  canBook: boolean;
  navigationState: StudentDirectoryNavigationState | undefined;
}) {
  const contact = studentRosterContact(student);
  const parent =
    student.parentName ?? (student.isUnder18 ? "Parent / carer" : null);
  const attention = studentDirectoryAttention(student);

  return (
    <article className="student-roster-card">
      <div className="student-roster-card-head">
        <div>
          <Link
            className="workflow-action-link student-roster-name"
            to={studentDetailHref(student.id, scope, returnTo)}
            state={navigationState}
          >
            {student.displayName ?? student.fullName}
          </Link>
          <div className="student-roster-learning small">
            <Learning student={student} />
          </div>
          {attention && (
            <div className="student-roster-attention-summary small">
              {attention}
            </div>
          )}
        </div>
        {student.status !== "active" && (
          <span className="tag">{student.status}</span>
        )}
      </div>

      <details className="student-roster-details">
        <summary>More details</summary>
        <dl className="student-roster-meta">
          {student.school && (
            <div>
              <dt>School</dt>
              <dd>{student.school}</dd>
            </div>
          )}
          <div>
            <dt>Next</dt>
            <dd>{lessonLabel(student.nextLessonAt)}</dd>
          </div>
          <div>
            <dt>Contact</dt>
            <dd>{contact ?? "No email on record"}</dd>
          </div>
          {parent && (
            <div>
              <dt>Parent</dt>
              <dd>{parent}</dd>
            </div>
          )}
          <div>
            <dt>Portal</dt>
            <dd>{portalLabel(student)}</dd>
          </div>
          {attention && (
            <div className="student-roster-attention">
              <dt>Attention</dt>
              <dd>{attention}</dd>
            </div>
          )}
        </dl>
      </details>

      <div className="student-roster-card-foot">
        <StudentActions
          student={student}
          scope={scope}
          returnTo={returnTo}
          canBook={canBook}
          navigationState={navigationState}
        />
      </div>
    </article>
  );
}

/** Nothing chosen, in every menu. The empty string is what a select carries. */
const ANY = "";

/** One filter menu. "Any" is always first, and is what clearing returns to. */
function Menu({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  value: string;
}) {
  if (options.length === 0) return null;

  return (
    <label className="small muted student-filter">
      <span>{label}</span>
      <select
        className={value !== "" ? "is-on" : undefined}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        <option value="">Any</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const SORT_LABELS: Record<StudentDirectorySort, string> = {
  name: "Name",
  "next-lesson": "Next lesson",
  "last-lesson": "Last lesson",
  "last-active": "Last portal activity",
};

function DeleteSavedViewButton({
  view,
  onDelete,
}: {
  view: SavedStudentView;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        className="quiet small"
        type="button"
        onClick={() => setOpen(true)}
      >
        Delete view
      </button>
      {open && (
        <ConfirmDialog
          title="Delete saved view?"
          description={`“${view.name}”`}
          confirmLabel="Delete view"
          confirmClassName="action-danger"
          onConfirm={() => {
            setOpen(false);
            onDelete();
          }}
          onClose={() => setOpen(false)}
        >
          <p className="small" style={{ margin: 0 }}>
            This removes the saved shortcut from this browser for this account.
            It does not change any student records or filters stored elsewhere.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

export function Students() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { me } = useAuth();
  const location = useLocation();
  const owner = isOwner(me);
  const schoolDomain = canManageSchoolDomain(me);
  const tutorRole = me?.roles.includes("tutor") === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = resolveStudentListScope(
    owner,
    tutorRole,
    searchParams.get("scope"),
  );
  const adminTools = studentAdminToolMode(owner, tutorRole, scope);
  const newStudentRequested = searchParams.get("new") === "1";
  const restoredQuery = studentDirectoryQueryFromState(location.state);

  const [typed, setTyped] = useState(restoredQuery);
  const [query, setQuery] = useState(restoredQuery);
  const [viewName, setViewName] = useState("");
  const [viewNameError, setViewNameError] = useState<string | null>(null);
  const hydratedView = useRef<string | null>(null);
  /** The search the page state was last reset for; see the effect below. */
  const lastSearch = useRef(restoredQuery);

  // No fallback bucket: until auth has supplied a real user id there is no
  // account whose browser-local preferences may be read or written.
  const accountId = me?.user.id ?? null;
  const [savedViews, setSavedViews] = useState<SavedStudentView[]>([]);

  useEffect(() => {
    if (accountId === null) {
      setSavedViews([]);
      return;
    }
    setSavedViews(readSavedStudentViews(accountId));
  }, [accountId]);

  // Built-ins inherit the currently resolved scope. In particular, an owner
  // who is looking at My students must not be widened back to the whole school
  // merely by selecting an operational shortcut.
  const builtIns = useMemo(() => builtInStudentViews(scope), [scope]);
  const activeViewId = searchParams.get("view");

  function safeViewState(state: StudentViewState): StudentViewState {
    const safeScope = resolveStudentListScope(owner, tutorRole, state.scope);
    return {
      ...state,
      scope: safeScope,
      tutor: safeScope === "all" ? state.tutor : "",
    };
  }

  const activeCustom = activeViewId?.startsWith("custom:")
    ? (savedViews.find((view) => `custom:${view.id}` === activeViewId) ?? null)
    : null;
  const activeDefinition = studentViewStateForId(
    activeViewId,
    builtIns,
    savedViews,
  );
  const currentState = studentViewStateFromParams(searchParams, scope, typed);
  const activeState = activeDefinition ? safeViewState(activeDefinition) : null;
  const activeModified = activeState
    ? !sameStudentViewState(currentState, activeState)
    : false;

  const page = resolveStudentDirectoryPage(searchParams.get("page"));
  const size = currentState.size;
  const sort = currentState.sort;
  const tutor = currentState.tutor;
  const instrument = currentState.instrument;
  const school = currentState.school;
  const status = currentState.status;
  const account = currentState.account;
  const upcoming = currentState.upcoming;
  const effectiveTutor = studentListTutorFilter(scope, tutor);

  const narrowed = [
    effectiveTutor,
    instrument,
    school,
    status,
    account,
    upcoming,
  ].some((value) => value !== ANY);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed), 250);
    return () => clearTimeout(timer);
  }, [typed]);

  /**
   * A *new search* starts again at page one. Changing the page does not.
   *
   * This used to run whenever the query string changed at all, and the query
   * string is where the page number lives — so pressing Next set `page=2` and
   * then, a quarter of a second later, this deleted it again. The last page of
   * any list was unreachable. The guard is the search itself, kept in a ref so
   * that re-running against a new `searchParams` cannot be mistaken for
   * somebody typing.
   */
  useEffect(() => {
    if (lastSearch.current === query) return;
    lastSearch.current = query;
    if (!searchParams.has("page")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    setSearchParams(next, { replace: true });
  }, [query, searchParams, setSearchParams]);

  // A custom view may contain a private free-text search. Its id is safe in the
  // URL; the search itself is deliberately rehydrated from browser storage.
  useEffect(() => {
    if (!activeViewId) {
      hydratedView.current = null;
      return;
    }

    const selected = studentViewStateForId(activeViewId, builtIns, savedViews);
    const hydrationKey = activeViewId.startsWith("custom:")
      ? accountId === null
        ? null
        : `${accountId}:${activeViewId}`
      : activeViewId;
    if (!selected || !hydrationKey || hydratedView.current === hydrationKey)
      return;

    hydratedView.current = hydrationKey;
    setTyped(selected.query);
    setQuery(selected.query);
  }, [accountId, activeViewId, builtIns, savedViews]);

  const options = useAsync<StudentFilterOptions>(
    () => getStudentFilterOptions(),
    [],
  );
  const { data, error, loading, reload } = useAsync(
    () =>
      get<StudentDirectoryList>("/admin/students", {
        q: query,
        page: String(page),
        limit: size,
        sort,
        ...(scope === "mine" ? { scope: "mine" } : {}),
        tutor: effectiveTutor,
        instrument,
        school,
        status,
        account,
        upcoming,
      }),
    [
      query,
      page,
      size,
      sort,
      scope,
      effectiveTutor,
      instrument,
      school,
      status,
      account,
      upcoming,
    ],
    { reloadOnMirrorRefresh: true },
  );

  function replaceViewState(
    nextState: StudentViewState,
    viewId: string | null = activeViewId,
  ) {
    const safe = safeViewState(nextState);
    setSearchParams(paramsForStudentView(searchParams, safe, viewId));
  }

  function changeViewState(patch: Partial<StudentViewState>) {
    replaceViewState({ ...currentState, ...patch });
  }

  function chooseScope(next: StudentListScope) {
    if (next === scope) return;
    // Preserve #262's privacy behaviour: changing workspace resets categorical
    // URL state, while free-text search remains browser-local state.
    setSearchParams(next === "mine" ? { scope: "mine" } : {});
  }

  function chooseView(viewId: string) {
    if (!viewId) {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      next.delete("page");
      setSearchParams(next);
      return;
    }

    const selected = studentViewStateForId(viewId, builtIns, savedViews);
    if (!selected) return;

    const safe = safeViewState(selected);
    setTyped(safe.query);
    setQuery(safe.query);
    setSearchParams(paramsForStudentView(searchParams, safe, viewId));
  }

  function persistViews(next: SavedStudentView[]): boolean {
    if (accountId === null) return false;
    setSavedViews(next);
    writeSavedStudentViews(accountId, next);
    return true;
  }

  function saveCurrentView() {
    const name = viewName.trim();
    if (!name || accountId === null) return;
    if (savedStudentViewNameTaken(savedViews, name)) {
      setViewNameError("A saved view with that name already exists.");
      return;
    }

    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `view-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const next = addSavedStudentView(savedViews, name, currentState, id);
    if (!persistViews(next)) return;
    setViewName("");
    setViewNameError(null);
    setSearchParams(
      paramsForStudentView(searchParams, currentState, `custom:${id}`),
    );
  }

  function renameActiveView() {
    if (!activeCustom || accountId === null) return;
    const name = window.prompt("Rename saved view", activeCustom.name);
    if (name === null || !name.trim()) return;
    if (savedStudentViewNameTaken(savedViews, name, activeCustom.id)) {
      window.alert("A saved view with that name already exists.");
      return;
    }
    persistViews(renameSavedStudentView(savedViews, activeCustom.id, name));
  }

  function deleteActiveView() {
    if (!activeCustom || accountId === null) return;
    persistViews(deleteSavedStudentView(savedViews, activeCustom.id));
    setSearchParams(paramsForStudentView(searchParams, currentState, null), {
      replace: true,
    });
  }

  function clearNewStudentLaunch() {
    if (!searchParams.has("new")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    changeViewState({
      tutor: ANY,
      instrument: ANY,
      school: ANY,
      status: ANY,
      account: ANY,
      upcoming: ANY,
    });
  }

  function choosePage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next);
  }

  const activeFilters: string[] = [];
  if (effectiveTutor !== ANY) {
    activeFilters.push(
      effectiveTutor === "none"
        ? "Tutor: No tutor recorded"
        : `Tutor: ${
            options.data?.tutors.find((one) => one.id === effectiveTutor)
              ?.name ?? "Selected tutor"
          }`,
    );
  }
  if (instrument !== ANY) activeFilters.push(`Instrument: ${instrument}`);
  if (school !== ANY) activeFilters.push(`School: ${school}`);
  if (status !== ANY) activeFilters.push(`Status: ${status}`);
  if (account !== ANY)
    activeFilters.push(
      `Hub account: ${account === "yes" ? "Linked" : "Not linked"}`,
    );
  if (upcoming !== ANY) activeFilters.push("Upcoming: None booked");

  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;
  const pages = Math.max(1, Math.ceil(total / limit));
  const shown = data?.students.length ?? 0;
  const hasCriteria = query.trim() !== "" || narrowed;
  const emptyMessage = hasCriteria
    ? "No students match this search or these filters."
    : scope === "mine"
      ? "No students are in your teaching roster yet."
      : "No students have been added yet.";
  const returnTo = studentListReturnHref(searchParams);
  const navigationState = studentDirectoryNavigationState(query);
  const canBook = me?.booking.staff === true;

  return (
    <div
      className={
        scope === "mine"
          ? "students-page students-roster-page"
          : "students-page"
      }
    >
      <div className="students-page-head">
        <div>
          <h1>{scope === "mine" ? "My students" : "Students"}</h1>
          <p className="lede">
            {scope === "mine"
              ? "Your teaching roster. See what needs attention and act without opening every record."
              : "Everyone on the books, with the next useful action in reach."}
          </p>
        </div>
        {!loading && (
          <div className="students-total" aria-live="polite">
            <strong>{total}</strong>
            <span>{total === 1 ? "student" : "students"}</span>
          </div>
        )}
      </div>

      {(tutorRole || owner) && (
        <div className="student-scope-switch" aria-label="Student list scope">
          {tutorRole && (
            <button
              type="button"
              className={scope === "mine" ? "small" : "quiet small"}
              aria-pressed={scope === "mine"}
              onClick={() => chooseScope("mine")}
            >
              My students
            </button>
          )}
          {owner && (
            <button
              type="button"
              className={scope === "all" ? "small" : "quiet small"}
              aria-pressed={scope === "all"}
              onClick={() => chooseScope("all")}
            >
              All students
            </button>
          )}
        </div>
      )}

      {adminTools === "admin" ? (
        <>
          <NewStudent
            onAdded={reload}
            openRequested={newStudentRequested}
            onDismiss={clearNewStudentLaunch}
          />
          <ImportStudents onImported={reload} />
        </>
      ) : adminTools === "tutor-private" ? (
        <NewStudent
          mode="tutor-private"
          onAdded={reload}
          openRequested={newStudentRequested}
          onDismiss={clearNewStudentLaunch}
        />
      ) : null}

      <section
        className="panel student-roster-controls"
        aria-label="Find students"
      >
        <div className="student-roster-search-row student-search-header">
          <label className="student-search">
            <strong>Student search</strong>
            <input
              type="search"
              placeholder="Name, email or parent"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="quiet"
            aria-expanded={filtersOpen}
            aria-controls="student-filters"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ""}
          </button>
        </div>
        <div id="student-filters" hidden={!filtersOpen}>
        <div className="student-filter-row">
          <label className="small muted student-filter">
            <span>View</span>
            <select
              value={activeViewId ?? ""}
              onChange={(event) => chooseView(event.target.value)}
            >
              <option value="">Current filters</option>
              <optgroup label="Built-in views">
                {builtIns.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name}
                  </option>
                ))}
              </optgroup>
              {savedViews.length > 0 && (
                <optgroup label="Saved in this browser">
                  {savedViews.map((view) => (
                    <option key={view.id} value={`custom:${view.id}`}>
                      {view.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          {activeViewId && activeModified && (
            <span className="tag">Modified</span>
          )}
          {activeCustom && (
            <>
              <button
                className="quiet small"
                type="button"
                onClick={renameActiveView}
              >
                Rename view
              </button>
              <DeleteSavedViewButton
                view={activeCustom}
                onDelete={deleteActiveView}
              />
            </>
          )}
        </div>

        <div className="student-filter-row">
          <label className="small muted student-filter">
            <span>Save current view</span>
            <input
              type="text"
              maxLength={80}
              placeholder="View name"
              value={viewName}
              aria-invalid={viewNameError !== null}
              onChange={(event) => {
                setViewName(event.target.value);
                setViewNameError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") saveCurrentView();
              }}
            />
          </label>
          <button
            className="quiet small"
            type="button"
            disabled={accountId === null || !viewName.trim()}
            onClick={saveCurrentView}
          >
            Save view
          </button>
          <span className="small muted">
            Custom views are saved in this browser for this account only.
          </span>
          {viewNameError && (
            <span className="small error" role="alert">
              {viewNameError}
            </span>
          )}
        </div>

        <div className="student-roster-search-row">
          <label className="small muted student-filter student-sort">
            <span>Sort</span>
            <select
              value={sort}
              onChange={(event) =>
                changeViewState({
                  sort: event.target.value as StudentDirectorySort,
                })
              }
            >
              {STUDENT_DIRECTORY_SORTS.map((option) => (
                <option key={option} value={option}>
                  {SORT_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <label className="small muted student-filter student-page-size">
            <span>Show</span>
            <select
              value={size}
              onChange={(event) =>
                changeViewState({ size: event.target.value })
              }
            >
              {STUDENT_PAGE_SIZES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="student-filter-row">
          {scope === "all" && (
            <Menu
              label="Tutor"
              onChange={(value) => changeViewState({ tutor: value })}
              options={[
                ...(options.data?.tutors ?? []).map((one) => ({
                  value: one.id,
                  label: one.name,
                })),
                { value: "none", label: "No tutor recorded" },
              ]}
              value={tutor}
            />
          )}
          <Menu
            label="Instrument"
            onChange={(value) => changeViewState({ instrument: value })}
            options={(options.data?.instruments ?? []).map((one) => ({
              value: one,
              label: one,
            }))}
            value={instrument}
          />
          <Menu
            label="School"
            onChange={(value) => changeViewState({ school: value })}
            options={(options.data?.schools ?? []).map((one) => ({
              value: one,
              label: one,
            }))}
            value={school}
          />
          <Menu
            label="Status"
            onChange={(value) => changeViewState({ status: value })}
            options={(options.data?.statuses ?? []).map((one) => ({
              value: one,
              label: one,
            }))}
            value={status}
          />
          <Menu
            label="Account"
            onChange={(value) => changeViewState({ account: value })}
            options={[
              { value: "yes", label: "Hub account linked" },
              { value: "no", label: "No Hub account linked" },
            ]}
            value={account}
          />
          {narrowed && (
            <button
              className="quiet small student-clear-filters"
              onClick={clearFilters}
              type="button"
            >
              Clear filters
            </button>
          )}
        </div>

        </div>

        {activeFilters.length > 0 && (
          <div
            className="student-active-filters"
            aria-label="Active student filters"
          >
            <span className="small muted">Active filters</span>
            <div className="student-filter-chips">
              {activeFilters.map((filter) => (
                <span className="tag" key={filter}>
                  {filter}
                </span>
              ))}
            </div>
            {/* The panel's own Clear button is hidden while it is collapsed. */}
            {!filtersOpen && (
              <button
                className="quiet small"
                onClick={clearFilters}
                type="button"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        <div className="student-results-summary small muted">
          {loading
            ? "Loading…"
            : `Showing ${shown} of ${total} · Sorted by ${SORT_LABELS[sort]}`}
        </div>

        {/*
          School pupils are a separate domain, not a missing filter.
          `docs/ARCHITECTURE_RULES.md` keeps them out of this list, so saying
          so here — with the way to reach them — is better than an owner
          concluding that half their teaching has gone missing.
        */}
        {schoolDomain && (
          <p className="small muted student-school-signpost">
            School pupils are not listed here. They live under{" "}
            <Link to="/schools">Schools</Link>, where you can list them by
            school and edit them together.
          </p>
        )}
      </section>

      {error && <div className="error">{error}</div>}

      {!loading && total === 0 && (
        <div className="panel">
          <p className="empty">{emptyMessage}</p>
        </div>
      )}

      {scope === "mine" ? (
        <section aria-label="My student roster">
          <div className="student-roster-grid">
            {(data?.students ?? []).map((student) => (
              <StudentRosterCard
                key={student.id}
                student={student}
                scope={scope}
                returnTo={returnTo}
                canBook={canBook}
                navigationState={navigationState}
              />
            ))}
          </div>
        </section>
      ) : (
        <>
          <div className="panel student-admin-table student-directory-desktop">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Lessons / tutor</th>
                  <th>School</th>
                  <th>Next lesson</th>
                  <th>Portal</th>
                  <th>Attention</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.students ?? []).map((student) => {
                  const attention = studentDirectoryAttention(student);
                  return (
                    <tr key={student.id}>
                      <td>
                        <Link
                          to={studentDetailHref(student.id, scope, returnTo)}
                          state={navigationState}
                        >
                          {student.displayName ?? student.fullName}
                        </Link>
                        {student.status !== "active" && (
                          <>
                            {" "}
                            <span className="tag">{student.status}</span>
                          </>
                        )}
                      </td>
                      <td className="small">
                        <Learning student={student} />
                      </td>
                      <td className="muted small">{student.school ?? "—"}</td>
                      <td className="small">
                        {lessonLabel(student.nextLessonAt)}
                      </td>
                      <td className="small">{portalLabel(student)}</td>
                      <td className="small student-attention-cell">
                        {attention ?? "—"}
                      </td>
                      <td>
                        <StudentActions
                          student={student}
                          scope={scope}
                          returnTo={returnTo}
                          canBook={canBook}
                          navigationState={navigationState}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <section className="student-directory-mobile" aria-label="Students">
            <div className="student-roster-grid">
              {(data?.students ?? []).map((student) => (
                <StudentRosterCard
                  key={student.id}
                  student={student}
                  scope={scope}
                  returnTo={returnTo}
                  canBook={canBook}
                  navigationState={navigationState}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {pages > 1 && (
        <nav className="student-pagination" aria-label="Student pages">
          <button
            className="quiet"
            disabled={page <= 1}
            onClick={() => choosePage(page - 1)}
          >
            Previous
          </button>
          <span className="muted small">
            Page {page} of {pages}
          </span>
          <button
            className="quiet"
            disabled={page >= pages}
            onClick={() => choosePage(page + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
}
